import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePayments } from '../../context/PaymentContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { formatCurrency } from '../../utils/format';
import Breadcrumb from '../common/Breadcrumb';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ArrowLeftIcon,
  PencilIcon,
 
} from '@heroicons/react/24/outline';

const PaymentView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getPaymentById } = usePayments();
  const { showLoading, hideLoading } = useLoading();
  const getRoutePath = useRoutePath();

  const [payment, setPayment] = useState(null);
  const hasFetched = useRef(false);

  const fetchPaymentData = useCallback(async () => {
    if (!id || hasFetched.current) return;
    hasFetched.current = true;
    showLoading();
    try {
      const response = await getPaymentById(id);
     
      if (response?.data) {
        setPayment(response.data);
      }
    } catch (error) {
      // silent
    } finally {
      hideLoading();
    }
  }, [id, getPaymentById, showLoading, hideLoading]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  if (!payment) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009efb] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  const invoice = payment?.invoice;
   
  return (
    <div className="min-h-screen ">
      <div className="container mx-auto px-4 py-6">
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Payment Details
                </h1>
                <p className="text-gray-600 mt-1 text-sm md:text-base">
                  Payment #{payment?.payment_number || payment?.id || '-'}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/payments'))}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>
              <button
                onClick={() => navigate(getRoutePath(`/payments/edit/${id}`))}
                className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </button>
            </div>
          </div>
        </div>
  
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Information */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Payment Information
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Payment Method</label>
                    <p className="text-base text-gray-900">{payment?.payment_method?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Payment Number</label>
                    <p className="text-base text-gray-900">{payment?.payment_number || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Memo</label>
                    <p className="text-base text-gray-900">{payment?.memo || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Information */}
            {invoice && (
              <div className="bg-white rounded-lg shadow-md">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                    Applied Invoice
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Invoice Number</label>
                      <Link to={getRoutePath(`/invoices/view/${invoice?.id}`)} className="text-base font-semibold text-[#009efb] hover:text-[#0089db]" target="_blank">
                        {invoice?.invoice_number || 'N/A'}
                      </Link>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Invoice Date</label>
                      <div className="flex items-center text-base text-gray-900">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {payment?.invoice?.formated_invoice_date || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Invoice Total</label>
                    
                      <p className="text-base text-gray-900">{formatCurrency(payment?.invoice?.job?.job_total| 0)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Total Amount</label>
                      <p className="text-base text-gray-900"> {formatCurrency(Number(payment?.total).toFixed(2))}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Amount Paid</label>
                      <p className="text-green-600">{formatCurrency(payment?.amount || 0)}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Amount Due</label>
                      <p className="text-base text-gray-900">{formatCurrency(payment?.amount_due || 0)}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                      <p className="text-base text-gray-900">{payment?.invoice?.job?.description || payment?.invoice?.description || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-md">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Payment Summary
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Amount</span>
                  <span className="font-semibold text-green-600">{formatCurrency(payment?.amount || 0)}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Created At</span>
                  <span className="text-sm text-gray-900">{payment?.formated_created_at || '-'}</span>
                </div>
                 
              </div>
            </div>
 
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentView;


